import React, { useState, useCallback, useEffect, useMemo } from 'react';

import {
  AnnotationsContent,
  getAnnotationUniqueId,
} from 'components/ChartTools';
import { CHART_VIEW_SETTINGS_FULL_NAME } from 'components/ChartTools';

import {
  GGChart,
  GGMetric,
  GGSubMetric,
  GGSeries,
  GGDateAxis,
  GGMetricsTooltip,
  GGAnnotationsMetric,
  GGNoSeriesPlaceholder,
} from '@gogame/components';
import { useProcessAsync, useMountedState } from '@gogame/hooks-ts';

import {
  COLUMN_ACTIVITY,
  COLUMN_METRIC,
  COLUMN_TRAFFIC,
  COLUMN_APPLICATIONS,
  COLUMN_NETWORKS,
  COLUMN_PLATFORMS,
} from 'scenes/KPI/constants';
import { useLoadAnnotations } from 'scenes/KPI/data';
import { useExportKPIChart } from 'scenes/KPI/hooks';
import { ChartActions } from './ChartActions';
import { useSum, useSummable } from './useSum';

const getNamesList = (data) =>
  Array.isArray(data) ? data.map(({ name }) => name).join(', ') : data.name;

const getMetricName = (rowData, fullName, order) => {
  const series = [
    COLUMN_APPLICATIONS,
    COLUMN_NETWORKS,
    COLUMN_METRIC,
    COLUMN_ACTIVITY,
    COLUMN_TRAFFIC,
    COLUMN_PLATFORMS,
  ];
  const orderedNames = order.filter((col) => {
    return series.includes(col);
  });

  return fullName
    ? orderedNames
        .map((name) => {
          return getNamesList(rowData[name]);
        })
        .join(' / ')
    : getNamesList(rowData[COLUMN_METRIC]);
};

const EMPTY_SERIES = [];
const KPIChart = ({
  onChangeSettings,
  onChangeViewSettings,
  onShowAnnotations,
  onHideAnnotations,
  annotationsSettings,
  viewSettings,
  settings,
  columnsOrder = [],
  period,
  series,
  visiblePeriod,
  visibleSeries,
  loading: kpiLoading,
  data,
  hiddenSeries,
  withPaper,
  tooltipPosStrategy,
  style,
  idColorGetter,
}) => {
  const [annotationsLoading, processAsync] = useProcessAsync();
  const exportKPIChart = useExportKPIChart(processAsync);
  const { trend, seriesSum } = settings;
  const { data: annotations, isLoading } = useLoadAnnotations(
    {
      period: period,
      annotationsSettings: annotationsSettings,
      series: series,
    },
    {
      enabled: annotationsSettings?.showAnnotations,
    }
  );

  const [hiddenMetrics, setHiddenMetrics] = useMountedState([]);
  const [annotationUniqueId] = useState(getAnnotationUniqueId);

  const sortMap = useMemo(() => {
    const ids = series.map(({ id }) => id);
    return ids.reduce((m, id, i) => {
      return {
        ...m,
        [id]: i,
      };
    }, {});
  }, [series]);

  const handleExportVisible = useCallback(() => {
    exportKPIChart({
      period: visiblePeriod,
      series: visibleSeries.filter(({ id }) => !hiddenSeries.includes(id)),
    });
  }, [visiblePeriod, visibleSeries, hiddenSeries]);

  const handleExportAll = useCallback(() => {
    exportKPIChart({ period: visiblePeriod, series: visibleSeries });
  }, [visiblePeriod, visibleSeries]);

  const visibleRows = React.useMemo(() => {
    if (!data) {
      return [];
    }
    return data.filter(({ id }) => !hiddenSeries.includes(id));
  }, [hiddenSeries, data, columnsOrder]); // columnsOrder has been added to dependencies for update charts tooltip and legend

  const summableSeries = useSummable(visibleRows);
  const sumRows = useSum(seriesSum ? summableSeries : EMPTY_SERIES);

  const handleToggleMetric = useCallback((id, state) => {
    setHiddenMetrics((h) => {
      return state
        ? [...h, id]
        : h.filter((hiddenMetricId) => hiddenMetricId !== id);
    });
  }, []);

  useEffect(() => {
    setHiddenMetrics([]);
  }, [series]);

  const loading = kpiLoading || annotationsLoading || isLoading;

  return (
    <div
      style={{
        height: '300px',
        maxHeight: '300px',
        display: 'flex',
        width: '100%',
        flexGrow: '1',
        ...style,
      }}
    >
      <GGChart
        legend={viewSettings.legend}
        brush={viewSettings.brush}
        type={viewSettings.type}
        loading={loading}
        title={'Chart'}
        basis={annotations ? 'calc(100% - 16px)' : undefined}
        style={{ height: '100%', margin: 0 }}
        colorGetter={idColorGetter}
        withPaper={withPaper}
        hiddenMetrics={hiddenMetrics}
        onMetricToggle={handleToggleMetric}
        actions={
          // added <div> for visible tooltip when IconButton is disabled
          <ChartActions
            annotationUniqueId={annotationUniqueId}
            annotationsSettings={annotationsSettings}
            onChangeViewSettings={onChangeViewSettings}
            onChangeSettings={onChangeSettings}
            settings={settings}
            summableSeries={summableSeries}
            visibleSeries={visibleSeries}
            handleExportVisible={handleExportVisible}
            handleExportAll={handleExportAll}
            viewSettings={viewSettings}
          />
        }
        sidebar={
          <AnnotationsContent
            id={annotationUniqueId}
            onShow={onShowAnnotations}
            onHide={onHideAnnotations}
            settings={annotationsSettings}
            items={annotations && annotations.items}
            period={annotations && annotations.period}
          />
        }
        sortBy={(a, b) => a - b}
      >
        {sumRows.map(
          ({ period, values, metric, activityType, baseSeriesId, id }) => (
            <GGSeries
              data={values}
              period={period}
              seriesKey={`sum_${baseSeriesId}`}
              key={`sum_${baseSeriesId}`}
              dateKey={'date'}
            >
              <GGMetric
                dataKey={'value'}
                colorKey={id}
                yAxisGroup={metric.chartAxisGroupId}
                valueType={metric.type}
                name={`${activityType.name} SUM: ${metric.name}`}
                mode={'SUM'}
                sortKey={sortMap[baseSeriesId]}
                id={`SUM: ${baseSeriesId}`}
              >
                {trend && (
                  <GGSubMetric
                    dataKey={'trend'}
                    name={`${activityType.name} TREND: SUM: ${metric.name}`}
                    mode={'TREND'}
                    type={'line'}
                    dashed={true}
                    id={id}
                  />
                )}
              </GGMetric>
            </GGSeries>
          )
        )}
        {visibleRows.map(({ id, period, values, metric, ...rowData }) => {
          const name = getMetricName(
            {
              metric,
              ...rowData,
            },
            viewSettings[CHART_VIEW_SETTINGS_FULL_NAME],
            columnsOrder
          );

          return (
            <GGSeries
              data={values}
              period={period}
              seriesKey={id}
              key={id}
              dateKey={'date'}
            >
              <GGMetric
                dataKey={'value'}
                colorKey={id}
                yAxisGroup={metric.chartAxisGroupId}
                valueType={metric.type}
                name={name}
                sortKey={sortMap[id]}
                id={id}
              >
                {trend && (
                  <GGSubMetric
                    dataKey={'trend'}
                    mode={'TREND'}
                    name={`TREND: ${name}`}
                    id={`trend${id}`}
                  />
                )}
              </GGMetric>
            </GGSeries>
          );
        })}

        {annotations && annotationsSettings?.showAnnotations && (
          <GGSeries
            data={annotations.rows}
            period={annotations.period}
            seriesKey={'an'}
          >
            <GGAnnotationsMetric dataKey={'annotations'} />
          </GGSeries>
        )}
        <GGMetricsTooltip posStrategy={tooltipPosStrategy} />
        <GGDateAxis template={'d. MMM'} />
        <GGNoSeriesPlaceholder
          text={
            <span style={{ display: 'block', textAlign: 'center' }}>
              Please choose one of your Presets or add series in order to load
              data. <br />
              Please note that data will load, only once you click “Apply”.
            </span>
          }
        />
      </GGChart>
    </div>
  );
};

export default KPIChart;
